import { isPlatformBrowser } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import {
  Component,
  computed,
  effect,
  Inject,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  DOCUMENT
} from '@angular/core'
import { Meta } from '@angular/platform-browser'
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet, Routes } from '@angular/router'

import { TranslateService } from '@ngx-translate/core'
import { environment } from 'environments/environment'
import { Toast } from 'primeng/toast'
import { filter } from 'rxjs'

import { isBaseAiEnabled } from 'app/_shared/helpers/base-points.helpers'
import { AnalyticsService } from 'app/_shared/services/analytics.service'
import { BasePointsService } from 'app/_shared/services/base-points.service'
import { GiftCardService } from 'app/_shared/services/gift-card.service'
import { OrderService } from 'app/_shared/services/order.service'
import { PromoCodeStorageService } from 'app/_shared/services/promo-code-storage.service'
import { GiftCardStore } from 'app/store/gift-card.store'
import { UserStore } from 'app/store/user.store'

import { FactoryComponent } from './_shared/components/factory/factory.component'
import { generateCSSData } from './_shared/helpers/design-helpers'
import { EchoService } from './_shared/services/EchoService'
import { AppConfigService } from './app-config.service'
import { routes } from './app.routes'
import { ConfigStore } from './store/config.store'

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Toast],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [EchoService]
})
export class AppComponent implements OnInit, OnDestroy {
  private echoService: EchoService = inject(EchoService)
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute)
  private appConfigService: AppConfigService = inject(AppConfigService)
  private userStore = inject(UserStore)
  protected apiUrl = environment.API_URL
  private http = inject(HttpClient)
  private analyticsService = inject(AnalyticsService)
  platformId = inject(PLATFORM_ID)
  configStore = inject(ConfigStore)
  meta = inject(Meta)
  private giftCardStore = inject(GiftCardStore)
  private giftCardService = inject(GiftCardService)
  private promoCodeStorage = inject(PromoCodeStorageService)
  private orderService = inject(OrderService)
  private basePointsService = inject(BasePointsService)

  user = computed(() => this.userStore.user())

  private storefrontId: number
  title = 'Storefront'

  constructor(
    @Inject(DOCUMENT) private document: Document,
    translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.activatedRoute.queryParams?.subscribe(params => {
      this.storefrontId = this.appConfigService.getAppConfig?.id
      if (params['preview']) {
        //TODO: Move this into new page called /preview and load preview data in store
        this.listenToPreviewChanges()
      }
    })

    const isBrowser = isPlatformBrowser(inject(PLATFORM_ID))
    translate.setFallbackLang('en')
    if (isBrowser) {
      const lang = localStorage.getItem('lang')
      if (lang) {
        translate.use(lang)
      } else {
        localStorage.setItem('lang', 'en')
        translate.use('en')
      }
    }

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        this.analyticsService.trackPageViews(e.urlAfterRedirects)
      })

    this.generateDynamicRoutes()
    this.watchAuthForGiftCards()
    this.watchAuthForBasePoints()
  }

  ngOnInit(): void {
    this.setFavicon(this.configStore.config().settings?.favicon || '')
    this.updateMetaTags()
    this.route.queryParams.subscribe(params => {
      const linkCode = params['code']
      if (linkCode) {
        // New `?code=` link always replaces any previously stored code.
        this.promoCodeStorage.save(linkCode)
        this.fetchGiftCardBalance(linkCode)
      } else {
        const stored = this.promoCodeStorage.load()
        if (stored) {
          this.fetchGiftCardBalance(stored)
        }
      }
    })
  }

  ngOnDestroy() {
    this.echoService.disconnect()
  }

  private fetchGiftCardBalance(code: string) {
    this.orderService.getGiftCardBalance(this.storefrontId, code).subscribe({
      next: response => {
        // Drop stale responses: if the stored code changed (or was cleared by
        // logout) while the request was in flight, ignore the response so
        // the applied gift-card state doesn't get re-populated after logout.
        if (this.promoCodeStorage.load() !== code) {
          return
        }
        this.giftCardStore.setGiftCard(code, response.data.balance)
      },
      error: () => {
        if (this.promoCodeStorage.load() !== code) {
          return
        }

        // Bad / expired code: drop in-memory state AND the stored value so we
        // don't keep retrying it on every navigation.
        this.giftCardStore.clearAppliedGiftCard()
        this.promoCodeStorage.clear()
      }
    })
  }

  private fetchAccountGiftCards(userEmail: string) {
    const storefrontId = this.appConfigService.getAppConfig?.id
    if (!storefrontId) {
      return
    }
    return this.giftCardService.getGiftCards(storefrontId).subscribe({
      next: cards => {
        if (this.userStore.isLoggedIn() && this.userStore.user()?.email === userEmail) {
          this.giftCardStore.setAccountCards(cards)
        }
      },
      error: () => this.giftCardStore.clearAccountCards()
    })
  }

  /**
   * Keep gift-card state in sync with the authenticated user.
   *
   * - On login (including initial boot when user is restored from storage):
   *   fetch the user's account gift cards so the navbar reflects them.
   * - On logout transition: clear the stored promo code, applied gift-card
   *   state and account-card state so the navbar doesn't leak the previous
   *   user's balance.
   */
  private watchAuthForGiftCards() {
    let wasLoggedIn = false
    effect(onCleanup => {
      const user = this.userStore.user()
      const loggedIn = this.userStore.isLoggedIn()

      if (isBaseAiEnabled(this.appConfigService.getAppConfig?.base_ai_enabled)) {
        this.promoCodeStorage.clear()
        this.giftCardStore.clearAppliedGiftCard()
        this.giftCardStore.clearAccountCards()
        wasLoggedIn = false
        return
      }

      if (loggedIn && user) {
        const subscription = this.fetchAccountGiftCards(user.email)
        onCleanup(() => subscription?.unsubscribe())
      } else if (wasLoggedIn) {
        this.promoCodeStorage.clear()
        this.giftCardStore.clearAppliedGiftCard()
        this.giftCardStore.clearAccountCards()
      }
      wasLoggedIn = loggedIn
    })
  }

  /**
   * Check Base eligibility as soon as a verified SSM user enters a Base storefront.
   * The balance endpoint resolves the Base account from the bearer-token identity.
   */
  private watchAuthForBasePoints(): void {
    let activeContext: string | null = null

    effect(() => {
      const config = this.appConfigService.getAppConfig
      const user = this.userStore.user()
      const token = this.userStore.token()
      const enabled = isBaseAiEnabled(config?.base_ai_enabled)
      const canCheck = enabled && !!config?.id && !!user?.email_verified_at && !!token
      const nextContext = canCheck ? `${config.id}:${user?.id}:${token}` : null

      if (nextContext === activeContext) return

      activeContext = nextContext
      this.basePointsService.clear()

      if (canCheck && nextContext) {
        this.basePointsService.check(config.id, nextContext)
      }
    })
  }

  updateMetaTags() {
    if (this.appConfigService.getAppConfig?.no_index_site) {
      this.meta.updateTag({
        name: 'robots',
        content: 'noindex, nofollow'
      })
    }
  }

  setFavicon(faviconUrl: string) {
    if (isPlatformBrowser(this.platformId)) {
      const link: HTMLLinkElement =
        document.querySelector("link[rel*='icon']") || document.createElement('link')
      link.type = 'image/png'
      link.rel = 'icon'
      link.href = faviconUrl
      document.head.appendChild(link)
    }
  }

  private listenToPreviewChanges() {
    this.echoService.listen(
      `App.Models.Storefront.${this.storefrontId}`,
      'StorefrontPreviewUpdate',
      () => {
        this.http.get<{ data: any }>(`${this.apiUrl}/storefronts/get-by-url`).subscribe({
          next: next => {
            // Parse preview data
            const previewData = JSON.parse(next.data.preview_data)

            if (!previewData.settings) {
              previewData.settings = {}
            }
            this.configStore.saveConfig(previewData)

            this.generateDynamicRoutes()
            const dynamicThemeSCSS = generateCSSData(previewData)
            const configElement = this.document.querySelector('#appConfigStyle')
            if (configElement) {
              configElement.innerHTML = dynamicThemeSCSS
            }

            // Send event to parent (Admin) that the data is received
            //  This triggers the event listener in Admin project
            window.parent.postMessage('Socket event received', '*')
          },
          error: error => {
            console.error(error)
          }
        })
      }
    )
  }

  private generateDynamicRoutes() {
    const updatedRoutes: Routes = [...routes]

    const home = updatedRoutes.find(r => r.path === '')
    home?.children?.push(
      ...(this.configStore.config()?.pages ?? []).map(page => ({
        path: page.url,
        component: FactoryComponent,
        // title: page.title,
        data: { pageConfig: page }
      }))
    )

    this.router.resetConfig(updatedRoutes)
  }
}
